<template>
  <div :class="['home flex-column', programStyle]">
    <div class="title-container flex-row">
      <div class="flex-column">
        <span class="title">{{ $t('messages') }}</span>
        <ul class="subtitle flex-row">
          <li @click="goToPrograms">{{ $t('programs') }}&nbsp;&gt;&nbsp;</li>
          <li @click="goToDealersList">{{ domain }}&nbsp;&gt;&nbsp;</li>
          <li>{{ $t('messages') }}</li>
        </ul>
      </div>
      <div class="right" v-if="appliedFilters">
        <FilterModal
          :appliedFiltersLength="appliedFilters.length"
          :valueMulti="activeFilters"
          @clear-filters="clearAllFilters"
          @updateMulti="updateMulti"
        />
      </div>
    </div>
    <div class="content flex-column" v-if="!addMessage">
      <div class="above-table flex-column">
        <div class="table-descriptors flex-row">
          <span>{{ $t('messages') }}</span>
          <button @click="newMessage">{{ $t('add-new-message') }}</button>
        </div>
      </div>
      <div class="table-container flex-column">
        <DataTable :headings="messageListTH" :rows="messageConfigs" :perPage="numPerPage" :page="currentPage">
          <template v-slot:viewSlot="slotProps">
            <Eyecon class="eyecon" @click="getSingleMessageConfig(slotProps.row)"></Eyecon>
          </template>
          <template v-slot:copySlot="slotProps">
            <Copy class="copy-icon" @click="copyMessage(slotProps.row)"></Copy>
          </template>
          <template v-slot:deleteSlot="slotProps">
            <Delete v-if="!slotProps.row.isDeleted" class="delete-icon" @click="openModal(slotProps.row)"></Delete>
          </template>
        </DataTable>
        <div v-if="messageConfigs.length === 0" class="empty-table-message">
          <span v-if="this.appliedFilters.length > 0"
            >Sorry, we couldn't find anything that matches that criteria. Try broadening your search by selecting fewer
            filters.</span
          >
          <span v-else
            >There are no messages associated with this dealership yet. Start by
            <span class="attention" @click="newMessage">adding a new message.</span></span
          >
        </div>
        <div class="row-container" v-if="messageConfigs.length > 0">
          <div class="row-info">
            {{ $t('viewing-firstrow-lastrow-of-forms-totalcount', [firstRow, lastRow, totalCount]) }}
          </div>
          <GPagination
            v-if="pageCount > 1"
            class="pagination"
            v-model="page"
            :pageCount="pageCount"
            @changed="getMessageConfigsWithLoad"
          />
        </div>
      </div>
    </div>
    <div class="content flex-column above-table" v-if="addMessage">
      <div class="message-setup-container flex-column">
        <span class="sub-title">{{ $t('message-setup') }}</span>
        <div class="setup-row-1 flex-row">
          <Multiselect
            v-model="messageConfig.globalMessageType"
            :options="messageTypeOptions"
            class="multiselect single"
            :placeholder="$t('type')"
            :showLabels="false"
            :allow-empty="false"
            :disabled="messageConfig.status === 'Expired'"
          ></Multiselect>
          <Multiselect
            v-model="messageConfig.period"
            :options="timePeriodOptions"
            class="multiselect single"
            :placeholder="$t('time-period')"
            track-by="period"
            label="period"
            :showLabels="false"
            :allow-empty="false"
            :disabled="messageConfig.globalMessageType !== 'Time Period' || messageConfig.status === 'Expired'"
          ></Multiselect>
          <Multiselect
            v-model="messageConfig.priority"
            :options="priorityOptions"
            class="multiselect single"
            :placeholder="$t('priority')"
            :showLabels="false"
            :allow-empty="false"
            :disabled="messageConfig.status === 'Expired'"
          ></Multiselect>
        </div>
      </div>
      <div class="message-details-container flex-column">
        <span class="sub-title">{{ $t('message-details') }}</span>
        <p>{{ $t('message-copy') }}</p>
        <GInput
          v-if="messageConfig.status !== 'Expired'"
          type="textarea"
          v-model="messageConfig.globalMessage"
          maxlength="500"
          :showCount="true"
          class="input-box"
        ></GInput>
        <textarea v-if="messageConfig.status === 'Expired'" readonly v-model="messageConfig.globalMessage" />
        <div v-if="this.languageOptions.length > 1" class="message-translation-container">
          <p>{{ $t('message-copy-translation') }}</p>
          <Multiselect
            v-model="messageConfig.language"
            :options="languageOptions"
            class="multiselect single"
            :placeholder="$t('add-translation')"
            :showLabels="false"
            :allow-empty="false"
            :disabled="messageConfig.status === 'Expired'"
          ></Multiselect>
          <div v-if="addMessage !== 'new' && messageConfig.language !== 'English'">
            <p>{{ $t('translation-message-copy', [messageConfig.language]) }}</p>
            <textarea readonly v-model="messageConfig.translatedMessage" />
          </div>
        </div>
        <p>{{ $t('select-icon') }}</p>
        <Multiselect
          v-model="messageConfig.icon"
          :options="iconOptions"
          class="multiselect single"
          :placeholder="$t('icon')"
          :showLabels="false"
          :allow-empty="false"
          :disabled="messageConfig.status === 'Expired'"
        ></Multiselect>
      </div>
      <div class="schedule-message-container flex-column">
        <span class="sub-title">{{ $t('schedule-it') }}</span>
        <div class="date-container flex-row">
          <Collapsible
            class="start-date"
            :title="startDate ? dateFilter(startDate, 'calendarDate') : 'Start Date'"
            :canToggle="true"
            :contentAbsolute="true"
          >
            <v-date-picker
              v-model="startDate"
              mode="dateTime"
              :model-config="modelConfig"
              is24hr
              :popover="{ placement: 'right', visibility: 'click' }"
              :min-date="newDate"
              :input-props="{ class: 'w-full', placeholder: newDate, readonly: true }"
            />
          </Collapsible>
          <Collapsible
            :title="endDate ? dateFilter(endDate, 'calendarDate') : 'End Date'"
            :canToggle="true"
            :contentAbsolute="true"
            :invalid="!dateValidation(startDate, endDate)"
          >
            <v-date-picker
              v-model="endDate"
              mode="dateTime"
              :model-config="modelConfig"
              is24hr
              :popover="{ placement: 'right', visibility: 'click' }"
              :min-date="newDate"
              :input-props="{ class: 'w-full', placeholder: newDate, readonly: true }"
            />
          </Collapsible>
        </div>
      </div>
      <div class="flex-row button-container">
        <button
          v-if="addMessage === 'new'"
          :class="['add', submitValidation ? '' : 'disabled']"
          @click="submitMessageConfig"
          :disabled="!submitValidation"
        >
          {{ $t('add-message') }}
        </button>
        <button
          v-if="addMessage === 'edit'"
          :class="['add', submitValidation ? '' : 'disabled']"
          @click="editMessageConfig"
          :disabled="!submitValidation"
        >
          {{ $t('submit') }}
        </button>
        <button @click="clearMessage">{{ $t('cancel') }}</button>
      </div>
    </div>
    <Modal class="modal-screens" v-show="isModalOpen">
      <template v-slot:body class="body">
        {{ $t('delete-message') }}
        <div class="modal-button-container">
          <button class="submit" @click="deleteMessage">{{ $t('submit') }}</button>
          <button @click="isModalOpen = false">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import DataTable from '@/components/DataTable.vue';
import GPagination from '@/components/GComponents/GPagination.vue';
import GInput from '@/components/GComponents/GInput.vue';
import Collapsible from '@/components/Collapsible.vue';
import Multiselect from 'vue-multiselect';
import Modal from '@/components/Modal.vue';
import Eyecon from '@/assets/new/eye-con.svg';
import Copy from '@/assets/new/icon_copy.svg';
import Delete from '@/assets/new/icon_trash.svg';
import FilterModal from '@/components/FilterModal.vue';
import { mapState } from 'vuex';
import messageListTH from '@/utils/tableHeaders/messageListTH';
import 'vue-multiselect/dist/vue-multiselect.min.css';

const messageStatusFilterOptions = [
  {
    label: 'Active',
    checked: false,
  },
  {
    label: 'Expired',
    checked: false,
  },
  {
    label: 'Draft',
    checked: false,
  },
  {
    label: 'Scheduled',
    checked: false,
  },
];

const messagePriorityFilterOptions = [
  {
    label: 'Critical',
    checked: false,
  },
  {
    label: 'High',
    checked: false,
  },
  {
    label: 'Medium',
    checked: false,
  },
  {
    label: 'Informational',
    checked: false,
  },
];

const messageTypeFilterOptions = [
  {
    label: 'Program',
    checked: false,
  },
  {
    label: 'Time Period',
    checked: false,
  },
];

export default {
  name: 'CCMessages',
  components: {
    DataTable,
    Multiselect,
    GInput,
    Collapsible,
    GPagination,
    Eyecon,
    Copy,
    Delete,
    Modal,
    FilterModal,
  },
  data() {
    return {
      messageListTH: messageListTH(),
      messageConfigs: null,
      messageConfig: {
        domain: this.programName,
        entity: null,
        globalMessage: null,
        globalMessageType: null,
        icon: null,
        id: null,
        isDeleted: null,
        language: null,
        period: null,
        sender: null,
        status: null,
        priority: null,
      },
      numPerPage: 10,
      currentPage: 1,
      page: 1,
      totalCount: null,
      rowCountDropValue: { label: this.$t('show-10'), value: 10 },
      isModalOpen: false,
      tempRow: null,
      loader: null,
      addMessage: false,
      newDate: new Date(),
      messageTypeOptions: ['Program', 'Time Period'],
      iconOptions: ['Stop', 'Calendar', 'Yield'],
      languageOptions: [],
      timePeriodOptions: [],
      startDate: null,
      endDate: null,
      modelConfig: {
        type: 'string',
        timeAdjust: '00:00',
      },
      priorityOptions: ['Critical', 'High', 'Medium', 'Informational'],
      statusFilter: [],
      priorityFilter: [],
      typeFilter: [],
      appliedFilters: null,
      multiFilterOptions: {},
      messageStatusFilterOptions,
      messagePriorityFilterOptions,
      messageTypeFilterOptions,
    };
  },
  methods: {
    getMessageConfigs() {
      const { statusFilter, priorityFilter, typeFilter } = this;
      const { domain } = this;
      const loader = this.$loading.show();
      const page = this.page - 1;

      return this.$store
        .dispatch('getMessageConfigs', {
          domain,
          page,
          statusFilter,
          priorityFilter,
          typeFilter,
        })
        .then((res) => {
          this.totalCount = res.domainMessageCount;
          this.setMessageConfigs(res.domainMessages);
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    setMessageConfigs(res) {
      if (res) {
        res.forEach((message) => {
          // Set message status
          const startDate = new Date(message.startDate);
          const endDate = new Date(message.endDate);
          const currentDate = new Date();
          if (message.startDate === null && message.endDate === null) {
            message.status = 'Draft';
          } else if (startDate <= currentDate && endDate >= currentDate) {
            message.status = 'Active';
          } else if (startDate > currentDate) {
            message.status = 'Scheduled';
          } else {
            message.status = 'Expired';
          }

          // Set message type
          message.globalMessageType = message.globalMessageType === 'Domain' ? 'Program' : 'Time Period';

          // Set message period
          message.period = message.period === null ? 'N/A' : message.period;
        });
      }

      this.messageConfigs = res;
    },
    getMessageConfigsWithLoad() {
      const loader = this.$loading.show();
      this.getMessageConfigs();
      loader.hide();
    },
    newMessage() {
      this.getAll();

      this.addMessage = 'new';
      this.startDate = null;
      this.endDate = null;

      this.messageConfig = {
        domain: this.programName,
        entity: null,
        globalMessage: null,
        globalMessageType: null,
        icon: null,
        id: null,
        isDeleted: null,
        language: null,
        period: null,
        sender: null,
        status: null,
        priority: null,
      };
    },
    getAll() {
      const domain = this.programName;
      const loader = this.$loading.show();

      Promise.all([this.$store.dispatch('getDomains'), this.$store.dispatch('getAllPeriods', { domain })])
        .then((res) => {
          this.setLanguageTranslation(res[0]);
          this.setTimePeriodOptions(res[1]);
        })
        .finally(() => {
          loader.hide();
        });
    },
    setLanguageTranslation(res) {
      if (res) {
        const domainName = res.find((requiredDomain) => requiredDomain.domain === this.programName);
        if (domainName.languages.length > 1) {
          this.languageOptions.push('English', 'French');
        } else {
          this.languageOptions.push('English');
        }
      }
    },
    setTimePeriodOptions(res) {
      this.timePeriodOptions = res;
    },
    submitMessageConfig() {
      this.loader = this.$loading.show();

      let period = null;
      if (this.messageConfig.globalMessageType === 'Time Period') {
        ({ period } = this.messageConfig.period);
      }

      const messageType = this.messageConfig.globalMessageType === 'Program' ? 'Domain' : 'Domain Period';

      const language = this.messageConfig.language === null || this.messageConfig.language === 'English' ? 'EN' : 'FR';

      const params = {
        domain: this.programName,
        entity: this.messageConfig.entity,
        globalMessage: this.messageConfig.globalMessage,
        globalMessageType: messageType,
        icon: this.messageConfig.icon,
        language,
        period,
        startDate: this.startDate,
        endDate: this.endDate,
        priority: this.messageConfig.priority,
      };

      this.$store
        .dispatch('submitMessageConfig', params)
        .then((res) => {
          if (res) {
            this.getMessageConfigs();
            this.addMessage = false;
          }
        })
        .catch((error) => {
          this.$notify({
            group: 'primary',
            type: 'error',
            text: error.data.error,
          });
        })
        .finally(() => {
          this.loader.hide();
        });

      this.languageOptions = [];
    },
    getSingleMessageConfig(message) {
      const { id } = message;

      const domain = this.programName;
      const loader = this.$loading.show();

      Promise.all([
        this.$store.dispatch('getAllPeriods', { domain }),
        this.$store.dispatch('getDomains'),
        this.$store.dispatch('getSingleMessageConfig', { domain, id }),
      ])
        .then((res) => {
          this.setTimePeriodOptions(res[0]);
          this.setLanguageTranslation(res[1]);
          this.setSingleMessageConfig(res[2]);
          loader.hide();
        })
        .finally(() => {
          loader.hide();
        });
    },
    setSingleMessageConfig(message) {
      this.addMessage = 'edit';

      const index = this.timePeriodOptions.findIndex((item) => item.period === message.period);
      const period = this.timePeriodOptions[index];
      const messageType = message.globalMessageType === 'Domain' ? 'Program' : 'Time Period';
      const language = message.language === 'FR' ? 'French' : 'English';
      const messageId = this.messageConfigs.findIndex((item) => item.id === message.id);
      const { status } = this.messageConfigs[messageId];

      this.messageConfig = {
        domain: message.domain,
        entity: message.entity,
        globalMessage: message.globalMessage,
        globalMessageType: messageType,
        translatedMessage: message.translatedMessage,
        icon: message.icon,
        id: message.id,
        isDeleted: message.isDeleted,
        language,
        period,
        sender: message.sender,
        status,
        priority: message.priority,
      };

      this.startDate = message.startDate;
      this.endDate = message.endDate;
    },
    editMessageConfig() {
      this.loader = this.$loading.show();

      let period = null;
      if (this.messageConfig.globalMessageType === 'Time Period') {
        ({ period } = this.messageConfig.period);
      }

      const language = this.messageConfig.language === null || this.messageConfig.language === 'English' ? 'EN' : 'FR';

      const { id } = this.messageConfig;

      const params = {
        domain: this.programName,
        entity: this.messageConfig.entity,
        globalMessage: this.messageConfig.globalMessage,
        icon: this.messageConfig.icon,
        language,
        period,
        startDate: this.startDate,
        endDate: this.endDate,
        priority: this.messageConfig.priority,
      };

      this.$store
        .dispatch('editMessageConfig', { id, params })
        .then((res) => {
          if (res) {
            this.getMessageConfigs();
            this.addMessage = false;
          }
        })
        .catch((error) => {
          this.$notify({
            group: 'primary',
            type: 'error',
            text: error.data.error,
          });
        })
        .finally(() => {
          this.loader.hide();
        });

      this.languageOptions = [];
    },
    copyMessage(message) {
      const loader = this.$loading.show();

      const messageType = message.globalMessageType === 'Program' ? 'Domain' : 'Domain Period';

      const params = {
        domain: this.programName,
        entity: message.entity,
        globalMessage: `${message.globalMessage}(copy)`,
        globalMessageType: messageType,
        icon: message.icon,
        language: message.language,
        period: message.period,
        startDate: null,
        endDate: null,
        priority: message.priority,
      };

      this.$store
        .dispatch('submitMessageConfig', params)
        .then((res) => {
          if (res) {
            this.getMessageConfigs();
            this.addMessage = false;
          }
        })
        .catch((error) => {
          this.$notify({
            group: 'primary',
            type: 'error',
            text: error.data.error,
          });
        })
        .finally(() => {
          loader.hide();
        });

      this.languageOptions = [];
    },
    deleteMessage() {
      const loader = this.$loading.show();

      const { id } = this.tempRow;
      const domain = this.programName;

      this.$store
        .dispatch('deleteMessage', { domain, id })
        .then((res) => {
          if (res) {
            this.isModalOpen = false;
            loader.hide();
            this.getMessageConfigs();
          }
        })
        .catch((error) => {
          this.$notify({
            group: 'primary',
            type: 'error',
            text: error.data.error,
          });
        })
        .finally(() => {
          loader.hide();
        });
    },
    dateFilter(value, filter) {
      if (value && filter && this.$options.filters[filter]) {
        return this.$options.filters[filter](value);
      }
      return value;
    },
    goToPrograms() {
      this.$store.state.global.programName = null;
      this.$store.state.global.programImage = null;
      this.$router.push({
        name: 'Programs',
      });
      this.$store.state.activeNavTab = this.activeNavBar[0];
    },
    goToDealersList() {
      this.$router.push({
        name: 'Dealer-List',
      });
      this.$store.state.activeNavTab = this.activeNavBar[1];
    },
    dateValidation(startDate, endDate) {
      if (startDate > endDate) {
        return false;
      }
      return true;
    },
    clearMessage() {
      this.startDate = null;
      this.endDate = null;
      this.addMessage = false;
      this.languageOptions = [];
    },
    openModal(data) {
      this.tempRow = data;
      this.isModalOpen = true;
    },
    getFilters() {
      const StatusFilters = this.messageStatusFilterOptions;
      const PriorityFilters = this.messagePriorityFilterOptions;
      const TypeFilters = this.messageTypeFilterOptions;

      this.multiFilterOptions = {
        Status: StatusFilters,
        Priority: PriorityFilters,
        Type: TypeFilters,
      };
      this.createFilters();
    },
    createFilters() {
      this.statusFilter = [];
      this.priorityFilter = [];
      this.typeFilter = [];

      this.$store.state.activeMessagesFilters = this.multiFilterOptions;
      this.setAppliedFilters();
    },
    setAppliedFilters() {
      this.statusFilter = [];
      this.priorityFilter = [];
      this.typeFilter = [];

      const { statusFilter, priorityFilter, typeFilter } = this;

      let length = 0;
      if (this.activeFilters) {
        if (this.activeFilters.Status) {
          this.activeFilters.Status.forEach((option) => {
            if (option.checked) statusFilter.push(option.label);
          });
        }
        if (this.activeFilters.Priority) {
          this.activeFilters.Priority.forEach((option) => {
            if (option.checked) priorityFilter.push(option.label);
          });
        }
        if (this.activeFilters.Type) {
          this.activeFilters.Type.forEach((option) => {
            if (option.checked) {
              if (option.label === 'Program') {
                typeFilter.push('Domain');
              } else {
                typeFilter.push('Domain Period');
              }
            }
          });
        }
      }
      length = statusFilter.length + priorityFilter.length + typeFilter.length;
      this.appliedFilters = {
        statusFilter,
        priorityFilter,
        typeFilter,
        length,
      };
    },
    updateMulti(updates) {
      const loader = this.$loading.show();
      this.page = 1;
      this.multiFilterOptions = updates;
      this.$store.state.activeMessagesFilters = this.multiFilterOptions;
      this.setAppliedFilters();
      this.getMessageConfigs();
      loader.hide();
    },
    clearAllFilters() {
      if (this.multiFilterOptions.Status) {
        this.multiFilterOptions.Status.forEach((tag) => {
          this.$set(tag, 'checked', false);
        });
      }
      if (this.multiFilterOptions.Priority) {
        this.multiFilterOptions.Priority.forEach((tag) => {
          this.$set(tag, 'checked', false);
        });
      }
      if (this.multiFilterOptions.Type) {
        this.multiFilterOptions.Type.forEach((tag) => {
          this.$set(tag, 'checked', false);
        });
      }
      this.appliedFilters.length = 0;
      this.$store.state.activeMessagesFilters = this.multiFilterOptions;
    },
  },
  computed: {
    ...mapState('global', ['programName', 'programStyle']),
    ...mapState({
      domain: (state) => state.global.programName,
      activeFilters: 'activeMessagesFilters',
    }),
    pageCount() {
      if (this.messageConfigs) {
        const l = this.totalCount;
        const s = this.rowCountDropValue.value;
        return Math.ceil(l / s);
      }
      return null;
    },
    submitValidation() {
      if (this.messageConfig.status !== 'Expired') {
        if (this.messageConfig.globalMessageType === 'Time Period') {
          if (
            this.messageConfig.globalMessageType &&
            this.messageConfig.period &&
            this.messageConfig.priority &&
            this.messageConfig.globalMessage &&
            this.messageConfig.icon &&
            this.startDate &&
            this.endDate
          ) {
            return this.dateValidation(this.startDate, this.endDate);
          }
        }
        if (this.messageConfig.globalMessageType === 'Program') {
          if (
            this.messageConfig.globalMessageType &&
            this.messageConfig.priority &&
            this.messageConfig.globalMessage &&
            this.messageConfig.icon &&
            this.startDate &&
            this.endDate
          ) {
            return this.dateValidation(this.startDate, this.endDate);
          }
        }
      }
      return false;
    },
    firstRow() {
      if (this.messageConfigs.length > 0) {
        return (this.page - 1) * 10 + 1;
      }
      return 0;
    },
    lastRow() {
      return (this.page - 1) * 10 + this.messageConfigs.length;
    },
  },
  created() {
    if (this.domain) {
      this.getFilters();
      this.getMessageConfigs();
    } else {
      this.$router.push({
        name: 'Programs',
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.home {
  width: 100%;
  .title {
    font-size: 32px;
    font-weight: 100;
    text-transform: uppercase;
  }
  .subtitle {
    font-size: 14px;
    margin-top: 5px;
    list-style-type: none;
    margin-bottom: 0px;
    padding-left: 0px;
    li {
      cursor: pointer;
    }
    li:last-child {
      cursor: default;
      font-weight: bold;
    }
  }
}
.content {
  height: 100%;
  flex-wrap: wrap;
  margin-top: 50px;
  p {
    font-size: 18px;
    color: $primary-black;
  }
  .sub-title {
    text-align: left;
    font-size: 24px;
    margin-bottom: 10px;
  }
  .flexRow {
    display: flex;
    margin-top: 20px;
    input {
      margin-left: 20px;
    }
  }
  .flex-column {
    display: flex;
    flex-direction: column;
    text-align: left;
  }
  .small-title {
    color: black;
    font-size: 20px;
    margin-right: 20px;
  }
  .message-setup-container {
    margin-bottom: 50px;
  }
  .message-details-container {
    margin-bottom: 50px;
    .input-box {
      width: 50%;
    }
    textarea {
      height: 100px;
      width: 50%;
    }
  }
  .schedule-message-container {
    margin-bottom: 40px;
    .date-container {
      margin-top: 15px;
      p {
        padding: 0px 15px 0px 0px;
      }
      .start-date {
        margin-right: 20px;
      }
      ::v-deep {
        .filter {
          width: 200px !important;
        }
      }
    }
  }
  .setup-row-1 {
    margin: 15px 0px 20px 0px;
  }
  .multiselect {
    margin-right: 15px;
  }
  .button-container {
    margin: 40px 0;
    button {
      margin-right: 20px;
    }
  }
}
.above-table {
  padding: 0 40px;
}
.title-container {
  text-align: left;
  border-bottom: 1px #dfe4e8 solid;
  padding-left: 40px;
  padding-bottom: 25px;
  padding: 30px 50px 30px 30px;
  justify-content: space-between;
  .right {
    display: flex;
  }
}
.table-descriptors {
  justify-content: space-between;
  font-size: 24px;
  align-items: center;
  .right {
    display: flex;
    input {
      margin-right: 20px;
    }
  }
}
.role-description {
  margin-right: 10px;
}
.roles {
  &:not(:last-child)::after {
    content: ',';
    margin-right: 5px;
  }
}

.table-container {
  margin-top: 50px;
  .empty-table-message {
    display: flex;
    justify-content: center;
    margin: 40px 400px;
    span {
      font-size: 24px;
    }
    .attention {
      text-decoration: underline;
      cursor: pointer;
      margin-left: 3px;
      font-weight: bold;
    }
  }
}
.dates {
  margin-top: 10px;
}
.modal-button-container {
  display: flex;
  margin-top: 10px;
  justify-content: center;
  .submit {
    margin-right: 20px;
  }
}
.form-layout {
  margin-left: 10px;
}
</style>
